import { pickScopeStamp } from '@/lib/scope-stamp';
import { mergeWhere } from '@/lib/table-query';
import { dateOnlyOf, orgCalendarDate, toDateOnlyValue } from '@/lib/utils/custom-date/custom-date';
import { getOrganizationTimeZone } from '@/lib/utils/custom-date/org-time-zone';
import { parseFormDefinition } from '@/modules/shared/hr-form-engine';
import { getEmployeesByUserIds } from '@/modules/shared/hr-lookups';
import {
  applyPeerForward,
  assignCategoryForm,
  buildFormSnapshot,
  buildReviewFilter,
  collectPeerManagerIds,
  computePeerSelectionOutcome,
  createReviewData,
  deletePmReviewDetail,
  findReviewData,
  getPeerSelectionStatusForManager,
  getPmFormById,
  getPmFormCandidates,
  getPmReviewDetailsForReview,
  getPmReviews,
  insertPmReviewDetail,
  parseReviewData,
  PeerAssignment,
  PeerSelectionErrorKey,
  planPeerSelection,
  PmReview,
  PmReviewDetail,
  QueryParams,
  ReviewFilterValue,
  resolveLockedPeerSkills,
  resolveMatchingPmForm,
  serializeFormSnapshot,
  serializeReviewData,
  updatePmReview,
  upsertReviewData,
  validatePeerSelection,
  withSkillsLikert,
} from '@/modules/performance/hr-review-core';
import { PeerRequest } from '../types/peer-request.types';

/** Days a reviewee has to complete the self review that is requested once the first peers are chosen (legacy: today + 15). */
const SELF_REVIEW_DEADLINE_DAYS = 15;

/** Raised for business-rule failures so the UI can show a translated message instead of a raw error. */
export class PeerSelectionError extends Error {
  constructor(
    public readonly key:
      | PeerSelectionErrorKey
      | 'PEER_REVIEW_DATA_NOT_FOUND'
      | 'NO_PEER_FORM_FOR_CATEGORY'
      | 'TARGET_MANAGER_REQUIRED',
    public readonly detail?: string
  ) {
    super(key);
  }
}

/** The employee's local today (organization zone) plus `days`, as a date-only value (`YYYY-MM-DDT00:00:00.000Z`). */
const addDays = (days: number, now: Date = new Date()) => {
  const date = new Date(
    `${orgCalendarDate(now.toISOString(), getOrganizationTimeZone())}T00:00:00.000Z`
  );
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString();
};

/** A deadline copied from the review data (a picked "YYYY-MM-DD" or an older stored value) as a date-only value. */
const toDeadlineValue = (value?: string | null) => toDateOnlyValue(dateOnlyOf(value)) ?? null;

const toPeerRequest = (review: PmReview, managerId: string): PeerRequest | null => {
  const peerData = findReviewData(parseReviewData(review.ReviewDataJson), 'peer');
  if (!peerData) return null;
  return {
    review,
    peerData,
    status: getPeerSelectionStatusForManager(review, managerId),
    deadline: peerData.ReportingManagerDeadLine,
  };
};

export const getPeerRequests = async (
  managerId: string,
  filters: ReviewFilterValue,
  paging: Pick<QueryParams, 'pageNo' | 'pageSize' | 'order'> & {
    columnWhere?: Record<string, unknown>;
  }
): Promise<{ items: PeerRequest[]; totalCount: number }> => {
  const { columnWhere, ...rest } = paging;
  const page = await getPmReviews({
    ...rest,
    filter: mergeWhere(buildReviewFilter(filters, { PeerManagerIds: managerId }), columnWhere),
  });
  return {
    items: page.items
      .map((review) => toPeerRequest(review, managerId))
      .filter((row): row is PeerRequest => row !== null),
    totalCount: page.totalCount,
  };
};

export const getPeerRequestDetails = (pmReviewId: string) =>
  getPmReviewDetailsForReview(pmReviewId, 'peer');

export interface SavePeerSelectionParams {
  request: PeerRequest;
  managerId: string;
  peers: PeerAssignment[];
  skills: string[];
}

/**
 * Legacy CreatePmReviewDetail (ProcessPmReviewDetails), run client-side: validates the selection, resolves the form
 * for each peer category, creates / removes the peer requests and recomputes the review's peer step. Notifications and
 * e-mails the legacy backend sent to peers are not sent from here.
 */
export const savePeerSelection = async ({
  request,
  managerId,
  peers,
  skills,
}: SavePeerSelectionParams) => {
  const { review, peerData } = request;
  const existing = await getPeerRequestDetails(review.ItemId);

  const plan = planPeerSelection(existing, peers, managerId);
  const error = validatePeerSelection({
    plan,
    skills,
    hasLockedSkills: peerData.PeerSkills.length > 0,
    managerSelection: peerData.ManagerPeerSelections.find(
      (selection) => selection.ManagerId === managerId
    ),
  });
  if (error) throw new PeerSelectionError(error);

  const lockedSkills = resolveLockedPeerSkills(peerData, skills);
  const [reviewee] = await getEmployeesByUserIds([review.ReviewForId]);
  const peerProfiles = new Map(
    (await getEmployeesByUserIds(plan.toInsert.map((peer) => peer.peerUserId))).map((e) => [
      e.UserId,
      e,
    ])
  );

  // One form per peer category: reuse the one already assigned to the review, otherwise match by unit + designation.
  let assignments = peerData.PeerCategoryForms;
  const formByCategory = new Map<string, Awaited<ReturnType<typeof getPmFormById>>>();
  for (const category of new Set(plan.toInsert.map((peer) => peer.category))) {
    const assigned = assignments.find((assignment) => assignment.Category === category);
    let form = assigned ? await getPmFormById(assigned.PmFormId) : null;
    if (!form) {
      const candidates = await getPmFormCandidates(review.AssessmentType, 'peer', category);
      form =
        resolveMatchingPmForm(
          candidates,
          { DepartmentId: reviewee?.DepartmentId, DesignationId: reviewee?.DesignationId },
          { category }
        ) ?? null;
      if (!form) throw new PeerSelectionError('NO_PEER_FORM_FOR_CATEGORY', category);
      assignments = assignCategoryForm(assignments, {
        Category: category,
        PmFormId: form.ItemId,
        PmFormTitle: form.Title,
      });
    }
    formByCategory.set(category, form);
  }

  const now = new Date().toISOString();

  for (const detail of plan.toDelete) await deletePmReviewDetail(detail.ItemId);

  const inserted: PmReviewDetail[] = [];
  for (const peer of plan.toInsert) {
    const form = formByCategory.get(peer.category);
    if (!form) continue;
    const definition = withSkillsLikert(parseFormDefinition(form), lockedSkills);
    const profile = peerProfiles.get(peer.peerUserId);
    const input = {
      PmReviewId: review.ItemId,
      Name: review.Name,
      AssessmentType: review.AssessmentType,
      ReviewType: 'peer' as const,
      DesignationId: profile?.DesignationId ?? '',
      // Same stamp as the self review: the reviewee's current profile, falling back to the review's.
      ...pickScopeStamp({ ...pickScopeStamp(review), ...pickScopeStamp(reviewee) }),
      RequestedDateUtc: now,
      Timezone: getOrganizationTimeZone(),
      PeersDeadLine: toDeadlineValue(peerData.PeersDeadLine),
      Status: 'Pending' as const,
      PeerUserId: peer.peerUserId,
      ReportingManagerId: managerId,
      Category: peer.category,
      PmFormJson: serializeFormSnapshot(buildFormSnapshot(form, definition), definition),
    };
    await insertPmReviewDetail(input);
    inserted.push({ ...input, ItemId: '' });
  }

  const remaining = existing.filter(
    (detail) => !plan.toDelete.some((deleted) => deleted.ItemId === detail.ItemId)
  );
  const outcome = computePeerSelectionOutcome({
    review,
    peerData: { ...peerData, PeerSkills: lockedSkills, PeerCategoryForms: assignments },
    peerDetailsAfter: [...remaining, ...inserted],
    managerId,
    now,
  });

  let reviewData = upsertReviewData(parseReviewData(review.ReviewDataJson), outcome.peerData);
  const reviewPatch: Record<string, unknown> = {
    PeerList: outcome.peerList,
    PeerSelectedStatus: outcome.peerSelectedStatus,
  };

  if (plan.toInsert.length > 0) {
    const selfPatch = await requestSelfReviewOnFirstPeerSelection(
      review,
      reviewee,
      lockedSkills,
      reviewData
    );
    if (selfPatch) {
      reviewData = selfPatch.reviewData;
      reviewPatch.Self = 'IsRequested';
    }
  }

  await updatePmReview(review.ItemId, {
    ...reviewPatch,
    ReviewDataJson: serializeReviewData(reviewData),
    PeerManagerIds: collectPeerManagerIds(reviewData),
  });
};

/** Legacy RequestSelfReviewOnFirstPeerSelection: an annual review asks the reviewee for a self review once peers exist. */
const requestSelfReviewOnFirstPeerSelection = async (
  review: PmReview,
  reviewee: Awaited<ReturnType<typeof getEmployeesByUserIds>>[number] | undefined,
  skills: string[],
  reviewData: ReturnType<typeof parseReviewData>
) => {
  if (review.AssessmentType !== 'arf' || review.Self !== 'NotApplicable' || !reviewee) return null;

  const candidates = await getPmFormCandidates('arf', 'self');
  const selfForm = resolveMatchingPmForm(candidates, {
    DepartmentId: reviewee.DepartmentId,
    DesignationId: reviewee.DesignationId,
  });
  if (!selfForm) return null;

  const now = new Date().toISOString();
  const deadline = addDays(SELF_REVIEW_DEADLINE_DAYS);
  const definition = withSkillsLikert(parseFormDefinition(selfForm), skills);

  await insertPmReviewDetail({
    PmReviewId: review.ItemId,
    Name: review.Name,
    AssessmentType: 'arf',
    ReviewType: 'self',
    DesignationId: reviewee.DesignationId ?? '',
    ...pickScopeStamp({ ...pickScopeStamp(review), ...pickScopeStamp(reviewee) }),
    RequestedDateUtc: now,
    Timezone: getOrganizationTimeZone(),
    PeersDeadLine: deadline,
    Status: 'Pending',
    PeerUserId: review.ReviewForId,
    PmFormJson: serializeFormSnapshot(buildFormSnapshot(selfForm, definition), definition),
  });

  return {
    reviewData: upsertReviewData(
      reviewData,
      createReviewData('self', {
        RequestDate: now,
        SelfDeadLine: deadline,
        PmFormId: selfForm.ItemId,
        PmFormTitle: selfForm.Title,
      })
    ),
  };
};

export interface ForwardPeerReviewParams {
  request: PeerRequest;
  fromManagerId: string;
  toManagerIds: string[];
  deadline?: string;
}

/** Legacy ForwardPeerReview: hands the peer selection to other managers, recording the transfer chain. */
export const forwardPeerReview = async ({
  request,
  fromManagerId,
  toManagerIds,
  deadline,
}: ForwardPeerReviewParams) => {
  const targets = toManagerIds.filter((id) => id && id !== fromManagerId);
  if (targets.length === 0) throw new PeerSelectionError('TARGET_MANAGER_REQUIRED');

  const nextPeerData = applyPeerForward(request.peerData, {
    fromManagerId,
    toManagerIds: targets,
    deadline: deadline || null,
  });
  const reviewData = upsertReviewData(parseReviewData(request.review.ReviewDataJson), nextPeerData);

  await updatePmReview(request.review.ItemId, {
    ReviewDataJson: serializeReviewData(reviewData),
    PeerManagerIds: collectPeerManagerIds(reviewData),
  });
};
